import { Banknote, RotateCcw, Sparkles } from "lucide-react";
import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Admonition } from "@/components/ui/admonition";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import { useRequiredFieldHighlight } from "../hooks/useRequiredFieldHighlight";
import type { ContractFormValues } from "../schemas/contractFormSchema";
import { computeFirstPaymentDate } from "../utils/computeFirstPaymentDate";

import { FormMessageT } from "./FormMessageT";
import { FormSectionCard } from "./FormSectionCard";
import { InfoTooltip } from "./InfoTooltip";

const FREQUENCIES = ["MONTHLY", "QUARTERLY", "SEMI_ANNUAL", "ANNUAL"] as const;
const TIMINGS = ["IN_ARREARS", "IN_ADVANCE"] as const;
const COMMON_CURRENCIES = ["TRY", "USD", "EUR", "GBP", "JPY", "CHF"] as const;

/** Spec §11.15.6 — universal input chrome (visible borders in both themes). */
const INPUT_CHROME = "bg-background border border-input shadow-sm";

/** Spec §11.15.2 — pixel-perfect label container. */
const LABEL_FLEX =
  "flex items-center gap-1.5 text-sm font-medium text-foreground/90";

export interface PaymentInfoSectionProps {
  /**
   * Phase 6a.20h — engagement-scoped audit period end (ISO YYYY-MM-DD).
   * Drives two informational admonitions surfaced below the form grid
   * when the contract's `firstPaymentDate` falls after the audit
   * window. Optional — admonitions are hidden when omitted.
   */
  auditPeriodEnd?: string;
}

export function PaymentInfoSection({
  auditPeriodEnd,
}: PaymentInfoSectionProps = {}) {
  const { t } = useTranslation();
  const form = useFormContext<ContractFormValues>();
  const currency = useWatch({ control: form.control, name: "currency" });

  const amountHighlight = useRequiredFieldHighlight("payment.amount");
  const currencyHighlight = useRequiredFieldHighlight("currency");
  const frequencyHighlight = useRequiredFieldHighlight("payment.frequency");
  const firstPaymentHighlight = useRequiredFieldHighlight(
    "payment.firstPaymentDate",
  );

  // Phase 6e.4.5 — Bug C UX: auto-compute `payment.firstPaymentDate` from
  // (commencementDate, payment.timing, payment.frequency) so the auditor
  // is never asked to derive an arbitrary date. Manual override is
  // respected — once the field is dirty (auditor typed anything), this
  // hook becomes a no-op for that field.
  //
  // IFRS context: backend's `buildPaymentVector` uses `firstPaymentDate`
  // as the anchor for `monthIndex`. If the auditor leaves the field at
  // the commencementDate default, backend computes `monthIndex === 0`
  // for the first payment, which the calculator interprets as IN_ADVANCE
  // regardless of the timing radio. This was the trigger for Bug A+B in
  // real-world use (mathematical proof in the 65-test audit doc).
  const commencementDate = useWatch({
    control: form.control,
    name: "commencementDate",
  });
  const timing = useWatch({ control: form.control, name: "payment.timing" });
  const frequency = useWatch({
    control: form.control,
    name: "payment.frequency",
  });
  const firstPaymentDateWatch = useWatch({
    control: form.control,
    name: "payment.firstPaymentDate",
  });
  const isFirstPaymentDateDirty = Boolean(
    form.formState.dirtyFields.payment?.firstPaymentDate,
  );

  // Phase 6a.20h — two stacked admonitions when the first payment lands
  // outside the audit window. Both are informational and never block
  // submission. Admonition A (warning, ANNUAL + IN_ARREARS combo)
  // is a strict subset of B's trigger condition, so when A fires B
  // fires too — render A first then B underneath.
  const firstPaymentPostAudit = Boolean(
    auditPeriodEnd &&
      typeof firstPaymentDateWatch === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(firstPaymentDateWatch) &&
      firstPaymentDateWatch > auditPeriodEnd,
  );
  const showAnnualArrearsWarning =
    firstPaymentPostAudit &&
    frequency === "ANNUAL" &&
    timing === "IN_ARREARS";

  React.useEffect(() => {
    // Honor manual override — once the auditor types into the field,
    // we stop auto-updating it. (Grace-period edge case: a contract
    // might commence on Jan 1 but the first cash payment isn't due
    // until April 1 because of a 3-month rent-free clause. The auditor
    // sets it manually; our hook must not overwrite.)
    if (isFirstPaymentDateDirty) return;
    if (!commencementDate || !timing || !frequency) return;

    const computed = computeFirstPaymentDate(
      commencementDate,
      timing,
      frequency,
    );
    if (!computed) return;

    // `setValue` with `shouldDirty: false` so the auto-write doesn't
    // itself trigger the dirty flag (which would freeze further
    // auto-updates). The if-guard prevents an infinite render loop
    // when computed equals the current value.
    const currentValue = form.getValues("payment.firstPaymentDate");
    if (currentValue !== computed) {
      form.setValue("payment.firstPaymentDate", computed, {
        shouldDirty: false,
        // Re-run Zod so the date-format error clears.
        shouldValidate: true,
      });
    }
  }, [commencementDate, timing, frequency, isFirstPaymentDateDirty, form]);

  // Manual reset back to auto. Useful if the auditor typed something
  // and realizes the default was correct after all.
  const handleResetToAuto = React.useCallback(() => {
    if (!commencementDate || !timing || !frequency) return;
    const computed = computeFirstPaymentDate(
      commencementDate,
      timing,
      frequency,
    );
    if (!computed) return;
    // `resetField` clears the dirty flag so future timing/frequency
    // edits resume auto-update. RHF doesn't expose a direct
    // "clear dirty for one field" API; `resetField` with
    // `keepDirty: false` accomplishes it.
    form.resetField("payment.firstPaymentDate", {
      defaultValue: computed,
      keepDirty: false,
    });
  }, [commencementDate, timing, frequency, form]);

  return (
    <FormSectionCard
      title={t("form.section.paymentInfo")}
      description={t("form.section.paymentInfoDescription")}
      icon={<Banknote className="h-4 w-4" />}
    >
      {/* Amount + currency */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
        <FormField
          control={form.control}
          name="payment.amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_FLEX}>
                {t("form.paymentAmount")}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type="number"
                    min={0.01}
                    step={0.01}
                    inputMode="decimal"
                    placeholder={t("form.paymentAmountPlaceholder")}
                    {...field}
                    value={field.value ?? ""}
                    className={cn(
                      INPUT_CHROME,
                      amountHighlight,
                      "text-right pr-14 font-mono tabular-nums",
                    )}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    {currency || t("form.paymentAmountSuffixFallback")}
                  </span>
                </div>
              </FormControl>
              <FormMessageT />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_FLEX}>
                {t("form.currency")}
                <span className="text-destructive">*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className={cn(INPUT_CHROME, currencyHighlight)}>
                    <SelectValue placeholder="TRY" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {COMMON_CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessageT />
            </FormItem>
          )}
        />
      </div>

      {/* Cadence: frequency + first payment date */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
        <FormField
          control={form.control}
          name="payment.frequency"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_FLEX}>
                {t("form.paymentFrequency")}
                <span className="text-destructive">*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className={cn(INPUT_CHROME, frequencyHighlight)}>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {FREQUENCIES.map((f) => (
                    <SelectItem key={f} value={f}>
                      {t(`paymentFrequency.${f}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessageT />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="payment.firstPaymentDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_FLEX}>
                {t("form.firstPaymentDate")}
                <span className="text-destructive">*</span>
                <InfoTooltip
                  content={t("form.firstPaymentDateHelp")}
                  ariaLabel={t("form.firstPaymentDateHelpAria")}
                  data-testid="tooltip-first-payment-date"
                />
              </FormLabel>
              <FormControl>
                <Input
                  type="date"
                  {...field}
                  className={cn(INPUT_CHROME, firstPaymentHighlight)}
                />
              </FormControl>
              <div className="flex items-center gap-2 mt-1 text-xs">
                {!isFirstPaymentDateDirty ? (
                  <Badge
                    variant="secondary"
                    className="gap-1 font-normal"
                    data-testid="first-payment-auto-badge"
                  >
                    <Sparkles className="h-3 w-3" aria-hidden="true" />
                    {t("form.firstPaymentDateAutoBadge")}
                  </Badge>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleResetToAuto}
                    className="h-7 gap-1 px-2 text-xs"
                    data-testid="first-payment-reset-auto"
                  >
                    <RotateCcw className="h-3 w-3" aria-hidden="true" />
                    {t("form.firstPaymentDateResetToAuto")}
                  </Button>
                )}
              </div>
              <FormMessageT />
            </FormItem>
          )}
        />
      </div>

      {/* Timing — full width, two selectable cards */}
      <div className="mt-5">
        <FormField
          control={form.control}
          name="payment.timing"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_FLEX}>
                {t("form.paymentTiming")}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1"
                >
                  {TIMINGS.map((tm) => {
                    const radioId = `timing-${tm.toLowerCase()}`;
                    const active = field.value === tm;
                    return (
                      <label
                        key={tm}
                        htmlFor={radioId}
                        className={cn(
                          "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                          active
                            ? "border-primary/40 bg-primary/5 ring-1 ring-primary/20"
                            : "border-border hover:bg-muted/40",
                        )}
                      >
                        <RadioGroupItem
                          value={tm}
                          id={radioId}
                          data-testid={radioId}
                          className="mt-0.5"
                        />
                        <div className="grid gap-0.5 leading-tight">
                          <span className="text-sm font-medium">
                            {t(`paymentTiming.${tm}`)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t(`paymentTiming.${tm}_description`)}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </RadioGroup>
              </FormControl>
              <FormMessageT />
            </FormItem>
          )}
        />
      </div>

      {firstPaymentPostAudit && (
        <div className="mt-4 space-y-2">
          {showAnnualArrearsWarning && (
            <div data-testid="payment-annual-arrears-post-audit-warning">
              <Admonition type="warning">
                {t("engagement.contract.payment.annualArrearsPostAuditWarning")}
              </Admonition>
            </div>
          )}
          <div data-testid="payment-no-payments-in-audit-period">
            <Admonition type="info">
              {t("engagement.contract.payment.noPaymentsInAuditPeriod")}
            </Admonition>
          </div>
        </div>
      )}
    </FormSectionCard>
  );
}
